import React, { useState } from 'react';
import type {
  CdComparisonBar,
  CdContentAdvantage,
  CdContentGap,
  CdCrawlReportData,
  CdCrawlSubject,
  CdCrawlTabId,
  CdCrawledPage,
  CdKeywordCompare,
  CdScoreCard,
  CdStrength,
  CdTechnicalCheck,
  CdTechnicalStatus,
  CdTone,
  CdWeakness,
} from '../../growth-competitor-dossier/types';
import {
  CD_ACTION_STEPS,
  CD_COMPARISON_BARS,
  CD_CONTENT_ADVANTAGES,
  CD_CONTENT_GAPS,
  CD_CRAWLED_PAGES,
  CD_CRAWL_META,
  CD_CRAWL_PAGE_COUNT,
  CD_CRAWL_SUBJECTS,
  CD_KEYWORD_COMPARE,
  CD_SCORE_CARDS,
  CD_TECHNICAL_CHECKS,
  CD_TECHNICAL_INSIGHT,
  CD_THEIR_STRENGTHS,
  CD_THEIR_WEAKNESSES,
} from '../../growth-competitor-dossier/mockData';
import {
  CD_CRAWL_TABS,
  CD_PAGE_FILTERS,
  GROWTH_COMPETITOR_CRAWL_META,
} from '../../growth-competitor-dossier/config';
import { Icon } from '../Icon/Icon';
import { Segmented } from '../Segmented/Segmented';
import './growth-competitor-dossier.css';

const TONE: Record<CdTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: {
    accent: 'var(--terracotta)',
    tint: 'color-mix(in srgb, var(--terracotta) 12%, transparent)',
  },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function scoreTone(score: number): CdTone {
  if (score >= 70) return 'success';
  if (score >= 40) return 'gold';
  return 'terra';
}

const TECH_TONE: Record<CdTechnicalStatus, CdTone> = {
  good: 'success',
  warning: 'gold',
  bad: 'terra',
};

const TECH_LABEL: Record<CdTechnicalStatus, string> = {
  good: 'Pass',
  warning: 'Warn',
  bad: 'Fail',
};

function CdCard({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
    >
      {children}
    </div>
  );
}

function CdTag({ tone, children }: { tone: CdTone; children: React.ReactNode }) {
  const t = TONE[tone];
  return (
    <span
      className="inline-flex items-center rounded-[var(--r-pill)] px-2.5 py-0.5 text-[12px] font-semibold"
      style={{ background: t.tint, color: t.accent }}
    >
      {children}
    </span>
  );
}

function CdCardHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="mb-5 flex items-start justify-between gap-3">
      <div>
        <h3 className="m-0 text-[16px] font-bold text-[var(--ink-0)]">{title}</h3>
        {subtitle ? <p className="mt-1 text-[13px] text-[var(--ink-3)]">{subtitle}</p> : null}
      </div>
      {right}
    </div>
  );
}

function CdScoreRing({ score, size = 72 }: { score: number | null; size?: number }) {
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  if (score == null) {
    return (
      <svg width={size} height={size} aria-hidden="true">
        <circle cx={center} cy={center} r={radius} fill="none" stroke="var(--surface-mute)" strokeWidth="5" />
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="central"
          fill="var(--ink-3)"
          fontSize="16"
          fontWeight="600"
        >
          —
        </text>
      </svg>
    );
  }

  const clamped = Math.max(0, Math.min(100, score));
  const offset = circumference - (clamped / 100) * circumference;
  const accent = TONE[scoreTone(score)].accent;

  return (
    <svg width={size} height={size} aria-hidden="true">
      <circle cx={center} cy={center} r={radius} fill="none" stroke="var(--surface-mute)" strokeWidth="5" />
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={accent}
        strokeWidth="5"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${center} ${center})`}
      />
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="central"
        fill="var(--ink-0)"
        fontSize="18"
        fontWeight="700"
      >
        {score}
      </text>
    </svg>
  );
}

function CdScoreCardItem({ card }: { card: CdScoreCard }) {
  return (
    <CdCard className="px-6 py-6">
      <div className="flex items-center gap-4">
        <CdScoreRing score={card.score} />
        <div>
          <div className="text-[13px] font-semibold text-[var(--ink-0)]">{card.label}</div>
          <div className="mt-0.5 text-[12px] text-[var(--ink-3)]">{card.desc}</div>
          <div className="mt-1 text-[11px] text-[var(--ink-4)]">/100</div>
        </div>
      </div>
    </CdCard>
  );
}

function CdStrengthRow({ item }: { item: CdStrength }) {
  return (
    <div className="flex items-start gap-2.5 rounded-[var(--r-md)] bg-[var(--success-tint)] px-3.5 py-2.5">
      <Icon kind="check-circ" size={18} stroke={2} className="shrink-0 text-[var(--success)]" />
      <span className="text-[13px] leading-[1.5] text-[var(--ink-1)]">{item.text}</span>
    </div>
  );
}

function CdWeaknessRow({ item }: { item: CdWeakness }) {
  const high = item.severity === 'high';
  return (
    <div
      className="flex items-start gap-2.5 rounded-[var(--r-md)] px-3.5 py-2.5"
      style={{
        background: high
          ? 'color-mix(in srgb, var(--terracotta) 12%, transparent)'
          : 'var(--gold-tint)',
      }}
    >
      <Icon
        kind="alert"
        size={18}
        stroke={2}
        className="shrink-0"
        style={{ color: high ? 'var(--terracotta)' : 'var(--gold)' }}
      />
      <span className="text-[13px] leading-[1.5] text-[var(--ink-1)]">{item.text}</span>
    </div>
  );
}

function CdComparisonBarItem({ bar }: { bar: CdComparisonBar }) {
  const max = bar.maxVal ?? Math.max(bar.yours, bar.theirs) * 1.2;
  return (
    <div className="mb-4">
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[13px] font-medium text-[var(--ink-0)]">{bar.label}</span>
        <div className="flex gap-4 text-[12px] text-[var(--ink-3)]">
          <span>
            You: <strong className="text-[var(--success)]">{bar.yours}</strong>
          </span>
          <span>
            Them: <strong className="text-[var(--ai-3)]">{bar.theirs}</strong>
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1">
        <div className="h-1.5 flex-1 overflow-hidden rounded-[4px] bg-[var(--surface-mute)]">
          <div
            className="h-full rounded-[4px] bg-[var(--success)]"
            style={{ width: `${(bar.yours / max) * 100}%` }}
          />
        </div>
        <div className="h-1.5 flex-1 overflow-hidden rounded-[4px] bg-[var(--surface-mute)]">
          <div
            className="h-full rounded-[4px] bg-[var(--ai-3)]"
            style={{ width: `${(bar.theirs / max) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function CdContentGapRow({
  item,
  last,
  onGenerateArticle,
}: {
  item: CdContentGap;
  last: boolean;
  onGenerateArticle?: (topic: string) => void;
}) {
  return (
    <div
      className={[
        'flex items-center justify-between gap-3 py-3.5',
        last ? '' : 'border-b border-[var(--cream-2)]',
      ].join(' ')}
    >
      <div>
        <div className="text-[13px] font-semibold text-[var(--ink-0)]">{item.topic}</div>
        <div className="mt-0.5 text-[12px] text-[var(--ink-3)]">Est. traffic: {item.traffic}</div>
      </div>
      <div className="flex items-center gap-2">
        <CdTag tone={item.priority === 'High' ? 'terra' : 'gold'}>{item.priority}</CdTag>
        <button
          type="button"
          onClick={() => onGenerateArticle?.(item.topic)}
          className="cursor-pointer rounded-[var(--r-sm)] border border-[color-mix(in_srgb,var(--success)_45%,transparent)] bg-transparent px-3.5 py-1.5 text-[12px] font-semibold text-[var(--success)]"
        >
          Generate Article
        </button>
      </div>
    </div>
  );
}

function CdContentAdvantageRow({ item, last }: { item: CdContentAdvantage; last: boolean }) {
  return (
    <div className={['py-3.5', last ? '' : 'border-b border-[var(--cream-2)]'].join(' ')}>
      <div className="flex items-center justify-between">
        <div className="text-[13px] font-semibold text-[var(--ink-0)]">{item.topic}</div>
        <span className="text-[12px] text-[var(--ink-4)]">{item.traffic}</span>
      </div>
      <div className="mt-2 flex gap-4">
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-[var(--ink-3)]">You:</span>
          <CdTag tone="success">{item.yourRank}</CdTag>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-[var(--ink-3)]">Them:</span>
          <CdTag tone="terra">{item.theirRank}</CdTag>
        </div>
      </div>
    </div>
  );
}

function CdKeywordGapCell({ gap }: { gap: number }) {
  if (gap === -99) {
    return <span className="font-semibold text-[var(--terracotta)]">Not ranked</span>;
  }
  if (gap > 0) {
    return <span className="font-semibold text-[var(--success)]">+{gap} ahead</span>;
  }
  return <span className="font-semibold text-[var(--terracotta)]">{gap} behind</span>;
}

function CdKeywordRow({ row }: { row: CdKeywordCompare }) {
  return (
    <tr className="border-b border-[var(--cream-2)]">
      <td className="px-3 py-3 font-medium text-[var(--ink-0)]">{row.keyword}</td>
      <td className="px-3 py-3 text-[var(--ink-3)]">{row.volume}</td>
      <td className="px-3 py-3">
        <CdTag tone={row.you === '—' ? 'terra' : 'success'}>{row.you}</CdTag>
      </td>
      <td className="px-3 py-3">
        <CdTag tone="info">{row.them}</CdTag>
      </td>
      <td className="px-3 py-3">
        <CdKeywordGapCell gap={row.gap} />
      </td>
      <td className="px-3 py-3">
        {row.gap < 0 ? (
          <button
            type="button"
            className="cursor-pointer rounded-[var(--r-sm)] border-0 bg-[var(--success-tint)] px-3 py-1.5 text-[11px] font-semibold text-[var(--success)]"
          >
            {row.gap === -99 ? 'Create Page' : 'Improve'}
          </button>
        ) : null}
      </td>
    </tr>
  );
}

function CdTechnicalCheckItem({ check }: { check: CdTechnicalCheck }) {
  return (
    <CdCard className="px-6 py-6">
      <div className="flex items-start justify-between">
        <div className="text-[13px] font-semibold text-[var(--ink-0)]">{check.label}</div>
        <CdTag tone={TECH_TONE[check.status]}>{TECH_LABEL[check.status]}</CdTag>
      </div>
      <div className="my-2 text-[22px] font-bold text-[var(--ink-0)]">{check.value}</div>
      <div className="text-[11px] text-[var(--ink-4)]">Benchmark: {check.benchmark}</div>
    </CdCard>
  );
}

function CdCrawledPageRow({ page, baseUrl }: { page: CdCrawledPage; baseUrl: string }) {
  const typeTone: CdTone =
    page.type === 'Service' ? 'success' : page.type === 'Blog' ? 'info' : 'gold';
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[var(--cream-2)] py-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-[14px] font-semibold text-[var(--ink-0)]">
            {page.title}
          </span>
          <CdTag tone={typeTone}>{page.type}</CdTag>
        </div>
        <span className="mt-0.5 block truncate font-[var(--font-mono)] text-[12px] text-[var(--ink-4)]">
          {baseUrl}
          {page.url}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-6">
        <div className="text-center">
          <div className="text-[12px] text-[var(--ink-4)]">SEO</div>
          <span
            className="text-[15px] font-bold"
            style={{ color: TONE[scoreTone(page.seoScore)].accent }}
          >
            {page.seoScore}
          </span>
        </div>
        <div className="text-center">
          <div className="text-[12px] text-[var(--ink-4)]">Issues</div>
          <span
            className="text-[15px] font-bold"
            style={{
              color:
                page.issues <= 2
                  ? 'var(--success)'
                  : page.issues <= 4
                    ? 'var(--gold)'
                    : 'var(--terracotta)',
            }}
          >
            {page.issues}
          </span>
        </div>
        <div className="text-center">
          <div className="text-[12px] text-[var(--ink-4)]">Traffic</div>
          <span className="text-[13px] font-semibold text-[var(--ink-3)]">{page.traffic}</span>
        </div>
        <button
          type="button"
          className="inline-flex cursor-pointer items-center gap-1 rounded-[var(--r-sm)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3.5 py-1.5 text-[12px] font-medium text-[var(--ink-3)]"
        >
          Details
          <Icon kind="right" size={13} stroke={2.2} />
        </button>
      </div>
    </div>
  );
}

/* ── Tabs ── */

function CdOverviewTab({
  showYourSite,
  scores,
  pending = false,
}: {
  showYourSite: boolean;
  scores?: CdScoreCard[];
  pending?: boolean;
}) {
  if (pending) {
    return (
      <div className="rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--surface-mute)] px-6 py-10 text-center">
        <p className="m-0 text-[14px] font-semibold text-[var(--ink-1)]">Pipeline data pending</p>
        <p className="mt-2 text-[13px] text-[var(--ink-3)]">
          Crawl scores and insights will appear once the competitor pipeline finishes processing.
        </p>
      </div>
    );
  }

  const scoreCards = scores?.length ? scores : CD_SCORE_CARDS;

  return (
    <div className="space-y-6">
      <div className="cd-score-grid grid gap-4" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        {scoreCards.map((card) => (
          <CdScoreCardItem key={card.id} card={card} />
        ))}
      </div>

      <div className="cd-duo-grid grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <CdCard className="px-6 py-6">
          <CdCardHeader
            title="Their Strengths"
            subtitle="What they're doing well"
            right={<CdTag tone="success">4 found</CdTag>}
          />
          <div className="flex flex-col gap-3.5">
            {CD_THEIR_STRENGTHS.map((item) => (
              <CdStrengthRow key={item.id} item={item} />
            ))}
          </div>
        </CdCard>

        <CdCard className="px-6 py-6">
          <CdCardHeader
            title="Their Weaknesses"
            subtitle="Opportunities for your practice"
            right={<CdTag tone="terra">6 found</CdTag>}
          />
          <div className="flex flex-col gap-3.5">
            {CD_THEIR_WEAKNESSES.map((item) => (
              <CdWeaknessRow key={item.id} item={item} />
            ))}
          </div>
        </CdCard>
      </div>

      {showYourSite ? (
        <CdCard className="px-6 py-6">
          <CdCardHeader
            title="Head-to-Head Comparison"
            subtitle="Smile Dental (You) vs Bright Smiles Co."
            right={
              <div className="flex gap-3">
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[var(--success)]" />
                  <span className="text-[12px] text-[var(--ink-3)]">Your Site</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-[var(--ai-3)]" />
                  <span className="text-[12px] text-[var(--ink-3)]">Bright Smiles</span>
                </div>
              </div>
            }
          />
          {CD_COMPARISON_BARS.map((bar) => (
            <CdComparisonBarItem key={bar.id} bar={bar} />
          ))}
        </CdCard>
      ) : null}

      {/* ELVA strategic insight */}
      <div
        className="rounded-[var(--r-lg)] px-7 py-7 text-white"
        style={{ background: 'var(--grad-ink)' }}
      >
        <div className="mb-3.5 flex items-center gap-2.5">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--r-sm)] bg-white/20">
            <Icon kind="sparkle" size={18} stroke={2} className="text-white" />
          </span>
          <span className="text-[16px] font-bold">ELVA Strategic Insight</span>
        </div>
        <p className="m-0 mb-3 text-[14px] leading-[1.7] text-white/95">
          Bright Smiles has <strong>6 critical weaknesses</strong> you can exploit. Their biggest
          vulnerability is the lack of online booking — 67% of patients prefer booking online.
          Combined with their slow mobile site, they&apos;re likely losing 30-40% of mobile
          visitors.
        </p>
        <p className="m-0 mb-5 text-[14px] leading-[1.7] text-white/95">
          <strong>Top 3 actions to outcompete them:</strong>
        </p>
        <div className="cd-actions-grid grid gap-3" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {CD_ACTION_STEPS.map((item) => (
            <div
              key={item.num}
              className="rounded-[var(--r-md)] bg-white/[0.12] p-4"
            >
              <div className="mb-2 inline-flex h-6 w-6 items-center justify-center rounded-[var(--r-pill)] bg-white/25 text-[12px] font-bold">
                {item.num}
              </div>
              <div className="mb-1 text-[13px] font-bold">{item.title}</div>
              <div className="text-[12px] leading-[1.5] text-white/80">
                {item.desc}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-[var(--r-md)] border-0 bg-[var(--success)] px-6 py-3 text-[13px] font-bold text-white"
          >
            <Icon kind="sparkle" size={16} stroke={2} />
            Generate Action Plan
          </button>
          <button
            type="button"
            className="cursor-pointer rounded-[var(--r-md)] border border-white/30 bg-transparent px-6 py-3 text-[13px] font-semibold text-white"
          >
            Create Counter-Campaigns
          </button>
        </div>
      </div>
    </div>
  );
}

function CdContentTab({
  onGenerateArticle,
  contentGaps,
  contentAdvantages,
  keywordCompare,
}: {
  onGenerateArticle?: (topic: string) => void;
  contentGaps?: CdContentGap[];
  contentAdvantages?: CdContentAdvantage[];
  keywordCompare?: CdKeywordCompare[];
}) {
  const gaps = contentGaps?.length ? contentGaps : CD_CONTENT_GAPS;
  const advantages = contentAdvantages?.length ? contentAdvantages : CD_CONTENT_ADVANTAGES;
  const keywords = keywordCompare?.length ? keywordCompare : CD_KEYWORD_COMPARE;

  return (
    <div className="space-y-6">
      <div className="cd-duo-grid grid gap-4" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <CdCard className="px-6 py-6">
          <CdCardHeader
            title="Topics They Cover That You Don't"
            subtitle="Content gaps where you're losing potential traffic"
            right={<CdTag tone="terra">{`${gaps.length} gaps`}</CdTag>}
          />
          {gaps.map((item, index) => (
            <CdContentGapRow
              key={item.id}
              item={item}
              last={index === gaps.length - 1}
              {...(onGenerateArticle ? { onGenerateArticle } : {})}
            />
          ))}
        </CdCard>

        <CdCard className="px-6 py-6">
          <CdCardHeader
            title="Your Content Advantages"
            subtitle="Topics where you outperform them"
            right={<CdTag tone="success">{`${advantages.length} advantages`}</CdTag>}
          />
          {advantages.map((item, index) => (
            <CdContentAdvantageRow
              key={item.id}
              item={item}
              last={index === advantages.length - 1}
            />
          ))}

          <div className="mt-5 rounded-[var(--r-md)] bg-[var(--success-tint)] p-4">
            <div className="mb-1.5 flex items-center gap-1.5">
              <Icon kind="check-circ" size={18} stroke={2} className="text-[var(--success)]" />
              <span className="text-[13px] font-bold text-[var(--success)]">Keep Pushing</span>
            </div>
            <p className="m-0 text-[12px] leading-[1.5] text-[var(--ink-3)]">
              You&apos;re winning on emergency dental content. ELVA recommends creating 2 more
              supporting articles to lock in these rankings.
            </p>
          </div>
        </CdCard>
      </div>

      <CdCard className="px-6 py-6">
        <CdCardHeader
          title="Keyword Ranking Comparison"
          subtitle="How you rank vs. Bright Smiles for local dental keywords"
        />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr className="border-b-2 border-[var(--cream-2)]">
                {['Keyword', 'Search Volume', 'Your Rank', 'Their Rank', 'Gap', 'Action'].map(
                  (head) => (
                    <th
                      key={head}
                      className="px-3 py-2.5 text-left text-[12px] font-semibold text-[var(--ink-3)]"
                    >
                      {head}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {keywords.map((row) => (
                <CdKeywordRow key={row.id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      </CdCard>
    </div>
  );
}

function CdTechnicalTab({ checks }: { checks?: CdTechnicalCheck[] }) {
  const rows = checks?.length ? checks : CD_TECHNICAL_CHECKS;
  return (
    <div className="space-y-6">
      <div className="cd-tech-grid grid gap-4" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        {rows.map((check) => (
          <CdTechnicalCheckItem key={check.id} check={check} />
        ))}
      </div>

      <div
        className="rounded-[var(--r-lg)] px-6 py-6 text-white"
        style={{ background: 'var(--grad-ink)' }}
      >
        <div className="mb-3 flex items-center gap-2.5">
          <Icon kind="sparkle" size={18} stroke={2} className="text-white" />
          <span className="text-[15px] font-bold">ELVA Technical Advantage</span>
        </div>
        <p className="m-0 text-[14px] leading-[1.7] text-white/95">
          {CD_TECHNICAL_INSIGHT}
        </p>
      </div>
    </div>
  );
}

function CdPagesTab({
  subject,
  pages,
  pageCount,
}: {
  subject: CdCrawlSubject;
  pages?: CdCrawledPage[];
  pageCount?: number | null;
}) {
  const rows = pages?.length ? pages : CD_CRAWLED_PAGES;
  const totalPages = pageCount ?? CD_CRAWL_PAGE_COUNT;
  return (
    <div className="space-y-6">
      <CdCard className="px-6 py-6">
        <CdCardHeader
          title="Crawled Pages Analysis"
          subtitle={`${totalPages} pages found on ${subject.url}`}
          right={
            <div className="flex gap-2">
              {CD_PAGE_FILTERS.map((filter, index) => {
                const active = index === 0;
                return (
                  <button
                    key={filter}
                    type="button"
                    className="cursor-pointer rounded-[var(--r-sm)] px-3.5 py-1.5 text-[12px] font-medium"
                    style={{
                      border: `1px solid ${active ? 'color-mix(in srgb, var(--success) 45%, transparent)' : 'var(--cream-2)'}`,
                      background: active ? 'var(--success-tint)' : 'transparent',
                      color: active ? 'var(--success)' : 'var(--ink-3)',
                    }}
                  >
                    {filter}
                  </button>
                );
              })}
            </div>
          }
        />
        {rows.map((page) => (
          <CdCrawledPageRow key={page.id} page={page} baseUrl={subject.url} />
        ))}
      </CdCard>

      <div
        className="flex flex-wrap items-center justify-between gap-4 rounded-[var(--r-lg)] px-7 py-7"
        style={{ background: 'var(--grad-ink)' }}
      >
        <div className="text-white">
          <div className="mb-1.5 flex items-center gap-2">
            <Icon kind="sparkle" size={18} stroke={2} className="text-white" />
            <span className="text-[16px] font-bold">ELVA Found 6 Weak Pages to Outrank</span>
          </div>
          <p className="m-0 max-w-[620px] text-[13px] leading-[1.5] text-white/85">
            Their cosmetic dentistry, emergency dental, and 4 blog pages have low SEO scores. ELVA
            can generate optimized content to outrank them on these topics.
          </p>
        </div>
        <button
          type="button"
          className="inline-flex shrink-0 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 bg-[var(--cream-1)] px-7 py-3.5 text-[14px] font-bold text-[var(--ink-0)]"
        >
          <Icon kind="sparkle" size={16} stroke={2} />
          Generate Outranking Content
        </button>
      </div>
    </div>
  );
}

function CdCrawlSubjectCard({
  subject,
  active,
  onSelect,
}: {
  subject: CdCrawlSubject;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="min-w-[200px] shrink-0 cursor-pointer rounded-[var(--r-md)] px-5 py-3.5 text-left"
      style={{
        border: `2px solid ${active ? 'color-mix(in srgb, var(--success) 55%, transparent)' : 'var(--cream-2)'}`,
        background: active ? 'var(--success-tint)' : 'var(--cream-1)',
      }}
    >
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[14px] font-bold text-[var(--ink-0)]">{subject.name}</span>
        {active ? <span className="h-2 w-2 rounded-full bg-[var(--success)]" /> : null}
      </div>
      <div className="flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
        <span>{subject.distance}</span>
        <span>•</span>
        <span className="inline-flex items-center gap-1">
          <Icon kind="star" size={12} stroke={1.8} className="text-[var(--gold)]" />
          {subject.rating}
        </span>
        <span>•</span>
        <span>{subject.reviews} reviews</span>
      </div>
      <div className="mt-1.5 flex items-center gap-1 text-[11px] text-[var(--ink-4)]">
        <Icon kind="globe" size={13} stroke={1.8} />
        <span>{subject.url}</span>
      </div>
    </button>
  );
}

function CdCrawlTabContent({
  tab,
  subject,
  showYourSite,
  onGenerateArticle,
  report,
  pending = false,
}: {
  tab: CdCrawlTabId;
  subject: CdCrawlSubject;
  showYourSite: boolean;
  onGenerateArticle?: (topic: string) => void;
  report?: CdCrawlReportData;
  pending?: boolean;
}) {
  if (tab === 'content') {
    return (
      <CdContentTab
        {...(onGenerateArticle ? { onGenerateArticle } : {})}
        {...(report?.contentGaps?.length ? { contentGaps: report.contentGaps } : {})}
        {...(report?.contentAdvantages?.length ? { contentAdvantages: report.contentAdvantages } : {})}
        {...(report?.keywordCompare?.length ? { keywordCompare: report.keywordCompare } : {})}
      />
    );
  }
  if (tab === 'technical') {
    return <CdTechnicalTab {...(report?.technicalChecks?.length ? { checks: report.technicalChecks } : {})} />;
  }
  if (tab === 'pages') {
    return (
      <CdPagesTab
        subject={subject}
        {...(report?.crawledPages?.length ? { pages: report.crawledPages } : {})}
        {...(report?.pageCount != null ? { pageCount: report.pageCount } : {})}
      />
    );
  }
  return (
    <CdOverviewTab
      showYourSite={showYourSite}
      pending={pending}
      {...(report?.scores?.length ? { scores: report.scores } : {})}
    />
  );
}

export interface CompetitorCrawlReportViewProps {
  subjects?: CdCrawlSubject[];
  report?: CdCrawlReportData;
  loading?: boolean;
  onExport?: () => void;
  onRecrawl?: () => void;
  onGenerateArticle?: (topic: string) => void;
}

export function CompetitorCrawlReportView({
  subjects: subjectsProp,
  report,
  loading = false,
  onExport,
  onRecrawl,
  onGenerateArticle,
}: CompetitorCrawlReportViewProps = {}) {
  const meta = GROWTH_COMPETITOR_CRAWL_META;
  const controlled = subjectsProp !== undefined;
  const sourceSubjects = controlled ? subjectsProp : CD_CRAWL_SUBJECTS;
  const pending = controlled && report?.dataStatus === 'pipeline_pending';
  const [selectedId, setSelectedId] = useState<string>(sourceSubjects[0]?.id ?? '');
  const [tab, setTab] = useState<CdCrawlTabId>('overview');
  const [showYourSite, setShowYourSite] = useState(false);

  const subject =
    sourceSubjects.find((item) => item.id === selectedId) ?? sourceSubjects[0]!;

  return (
    <div className="growth-competitor-dossier-root relative mx-auto w-full max-w-[1320px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px] flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="search" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>

        <div className="flex shrink-0 gap-2.5">
          <button
            type="button"
            onClick={() => {
              setShowYourSite((current) => !current);
            }}
            className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-[var(--r-md)] px-4 text-[13px] font-semibold"
            style={{
              border: `1.5px solid ${showYourSite ? 'color-mix(in srgb, var(--success) 55%, transparent)' : 'var(--cream-2)'}`,
              background: showYourSite ? 'var(--success-tint)' : 'var(--cream-1)',
              color: showYourSite ? 'var(--success)' : 'var(--ink-1)',
            }}
          >
            {showYourSite ? <Icon kind="check" size={15} stroke={2.2} /> : null}
            Compare with Your Site
          </button>
          <button
            type="button"
            onClick={onExport}
            className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-[var(--r-md)] border-0 px-4 text-[13px] font-semibold text-white shadow-[var(--sh-1)]"
            style={{ background: 'var(--grad-ink)' }}
          >
            <Icon kind="download" size={16} stroke={2} />
            Export Report
          </button>
        </div>
      </div>

      {/* Competitor selector strip */}
      <div className="mb-7 flex gap-3 overflow-x-auto pb-1">
        {sourceSubjects.map((item) => (
          <CdCrawlSubjectCard
            key={item.id}
            subject={item}
            active={item.id === selectedId}
            onSelect={() => {
              setSelectedId(item.id);
            }}
          />
        ))}
      </div>

      <div className="mb-6">
        <Segmented
          options={CD_CRAWL_TABS.map((t) => ({ value: t.id, label: t.label }))}
          value={tab}
          onChange={(value) => {
            setTab(value as CdCrawlTabId);
          }}
        />
      </div>

      <CdCrawlTabContent
        tab={tab}
        subject={subject}
        showYourSite={showYourSite}
        pending={pending}
        {...(report ? { report } : {})}
        {...(onGenerateArticle ? { onGenerateArticle } : {})}
      />

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[var(--cream-2)] pt-4">
        <span className="text-[12px] text-[var(--ink-4)]">
          Last crawled: {report?.crawlMeta?.lastCrawled ?? CD_CRAWL_META.lastCrawled} ·{' '}
          {report?.pageCount ?? CD_CRAWL_PAGE_COUNT} pages analyzed ·{' '}
          {report?.crawlMeta?.nextCrawl ?? CD_CRAWL_META.nextCrawl}
        </span>
        <button
          type="button"
          onClick={onRecrawl}
          className="cursor-pointer rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2 text-[12px] font-medium text-[var(--ink-3)] hover:bg-[var(--surface-mute)]"
        >
          Re-crawl Now
        </button>
      </div>
    </div>
  );
}
